/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import { useFormikContext } from 'formik';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, capitalize } from '@mui/material';
import PaymentMethodForm from './components/PaymentMethodForm';
import { APM_KEY } from '../../../pages/ConnectionsPage/components/constant';
import { Button, ButtonRounded, CircularLoader } from '../../atoms';
import { FlexContainer } from '../../atoms/flex/FlexContainer';
import useGenerateBlock from './hook/useGenerateBlock';
import ConnectionForm from './components/ConnectionForm';
import { getConnectionModalDescription } from './constant';

const FormNewRequestForm = ({
  data,
  setShowModal,
  loading,
  isModal,
  action,
  animatedLabel,
  customButtonProps,
  isConnectionForm,
  connectionName,
  connectionLogo,
  handleClose,
  multipleFlowEnabled,
  connectionCategory,
}) => {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);
  const { handleSubmit } = useFormikContext();
  const { generateBlockFn } = useGenerateBlock();
  const description = getConnectionModalDescription(connectionName, connectionCategory);

  const { blocks = [], form = '', supportedPaymentMethods = [] } = data;
  const isApmForm = form === APM_KEY && multipleFlowEnabled;

  const handleSubmitHandler = () => {
    setShowErrors(true);
    handleSubmit();
  };

  const generateButton = () => {
    if (animatedLabel) {
      return (
        <>
          <Button
            type="button"
            margin={`${isModal ? '40px' : 0} 16px 0 0`}
            onClick={handleSubmitHandler}
            loading={loading}
            className="blue"
            disabled={loading}
          >
            {t(`buttonsText.confirmAction.${action}`)}
          </Button>

          {data.closeBtn && (
            <Button likeDisabled onClick={setShowModal}>
              {capitalize(t('buttonsText.Close') ?? '')}
            </Button>
          )}
        </>
      );
    }

    return (
      <FlexContainer justifyContent="flex-end" width="100%">
        {!data.hideClose && data.closeBtn && (
          <Box mr="16px">
            <ButtonRounded type="button" variant="contained" color="secondary" onClick={setShowModal}>
              {capitalize(t('buttonsText.Close') ?? '')}
            </ButtonRounded>
          </Box>
        )}

        {!data.hideSubmit && (
          <ButtonRounded
            type="button"
            disabled={loading}
            variant="contained"
            color="primary"
            onClick={data.isComplete ? handleClose : handleSubmitHandler}
          >
            {loading ? (
              <CircularLoader size={20} bgcolor="#fff" />
            ) : (
              capitalize(t(data.isComplete ? 'Complete' : 'Continue') ?? '')
            )}
          </ButtonRounded>
        )}
      </FlexContainer>
    );
  };

  return (
    <>
      {isApmForm && <PaymentMethodForm supportedPaymentMethods={supportedPaymentMethods} />}

      {isConnectionForm && !isApmForm && (
        <ConnectionForm
          connectionName={connectionName}
          connectionLogo={connectionLogo}
          description={description}
          form={blocks}
          showErrors={showErrors}
          setShowErrors={setShowErrors}
          animatedLabel={animatedLabel}
        />
      )}

      {!isConnectionForm && (
        <Box>
          {blocks.map((item, index) => (
            <Box key={`${item.type}-${item.block_id}-${index}`}>{generateBlockFn(item)}</Box>
          ))}
        </Box>
      )}

      <Box
        display="flex"
        width="100%"
        margin={isModal ? 'auto' : '20px 0 0'}
        {...(customButtonProps && { ...customButtonProps })}
      >
        {generateButton()}
      </Box>
    </>
  );
};

FormNewRequestForm.propTypes = {
  data: shape({}).isRequired,
  setShowModal: func.isRequired,
  loading: bool,
  isModal: bool,
  action: string,
  animatedLabel: bool,
  customButtonProps: shape({}),
  isConnectionForm: bool,
  connectionName: string,
  connectionLogo: string,
  handleClose: func,
  multipleFlowEnabled: bool,
  connectionCategory: string,
};

FormNewRequestForm.defaultProps = {
  loading: false,
  isModal: false,
  action: 'submit',
  animatedLabel: true,
  customButtonProps: undefined,
  isConnectionForm: false,
  connectionName: '',
  connectionLogo: null,
  handleClose: () => {},
  multipleFlowEnabled: false,
  connectionCategory: '',
};

export default withRouter(FormNewRequestForm);
